import { motion, AnimatePresence } from 'framer-motion';
import { usePerfume } from '../context/PerfumeContext';
import { useLanguage } from '../context/LanguageContext';
import { essenceData } from '../data/translations';
import { Droplets } from 'lucide-react';

export const FormulaPanel = () => {
  const { getFormulaPercentages, totalVolume, maxVolume, fillPercent } = usePerfume();
  const { t } = useLanguage();

  const formulaItems = getFormulaPercentages();
  const isEmpty = formulaItems.length === 0;

  return (
    <motion.div
      className="bg-card/50 backdrop-blur-sm rounded-2xl border border-border/50 p-6 w-full max-w-xs"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-serif text-foreground tracking-wide">
          {t('yourFormula')}
        </h3>
        <Droplets className="w-5 h-5 text-muted-foreground" />
      </div>

      {/* Volume indicator */}
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-muted-foreground">{t('totalVolume')}</span>
          <span className="text-foreground font-medium">
            {totalVolume} / {maxVolume} {t('ml')}
          </span>
        </div>
        
        {/* Progress bar */}
        <div className="h-2 bg-secondary rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ 
              background: 'linear-gradient(90deg, hsl(var(--accent)) 0%, hsl(40, 90%, 60%) 100%)'
            }}
            initial={{ width: 0 }}
            animate={{ width: `${fillPercent}%` }}
            transition={{ type: 'spring', stiffness: 100, damping: 15 }}
          />
        </div>
      </div>

      {/* Formula list */}
      <div className="space-y-1 min-h-[200px]">
        <AnimatePresence mode="popLayout">
          {isEmpty ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center h-[200px] text-center"
            >
              <div className="w-12 h-12 rounded-full bg-secondary/50 flex items-center justify-center mb-3">
                <Droplets className="w-6 h-6 text-muted-foreground/50" />
              </div>
              <p className="text-muted-foreground text-sm">
                {t('emptyBottle')}
              </p>
              <p className="text-muted-foreground/60 text-xs mt-1">
                {t('emptyBottleHint')}
              </p>
            </motion.div>
          ) : (
            formulaItems.map((item, index) => {
              const essence = essenceData.find(e => e.id === item.essenceId);
              
              return (
                <motion.div
                  key={item.essenceId}
                  className="formula-item"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ delay: index * 0.05 }}
                  layout
                >
                  <div className="flex items-center gap-3">
                    {/* Color indicator */}
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: essence?.color }}
                    />
                    
                    {/* Name */}
                    <span className="text-sm text-foreground">
                      {t(`essences.${item.essenceId}`)}
                    </span>
                  </div>

                  {/* Percentage and drops */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      {item.drops} {item.drops === 1 ? t('dropAdded') : t('dropsAdded')}
                    </span>
                    <span className="text-sm font-medium text-foreground tabular-nums w-12 text-right">
                      {item.percentage}%
                    </span>
                  </div>
                </motion.div>
              );
            })
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
